<template>
    <div class="survey-wizard">
        <div class="survey-wizard__header">
            <h1>{{ headerTitle }}</h1>
            <v-spacer></v-spacer>
            <v-btn
                outlined
                color="primary"
                @click="$router.push({ name: 'surveys' })"
            >
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t('surveys.wizard.back') }}
            </v-btn>
        </div>

        <div v-if="!isViewMode" class="survey-wizard__steps">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="survey-wizard__step"
                :class="{
                    'survey-wizard__step--active': currentStep === index,
                    'survey-wizard__step--completed': currentStep > index
                }"
            >
                <div class="survey-wizard__step-number">{{ index + 1 }}</div>
                <span class="survey-wizard__step-label">{{ step.label }}</span>
            </div>
        </div>

        <div class="survey-wizard__content">
            <step-general-data
                v-if="currentStep === 0"
                :form="surveyForm"
                :readonly="isViewMode"
                @update="updateGeneralData"
            />

            <step-questions
                v-if="currentStep === 1"
                :questions="surveyForm.questions"
                :question-types="questionTypes"
                :readonly="isViewMode"
                @update="updateQuestions"
                @valid="setQuestionsValidity"
            />

            <step-preview
                v-if="currentStep === 2"
                :survey="surveyForm"
            />

            <step-configuration
                v-if="currentStep === 3"
                :form="surveyForm"
                :readonly="isViewMode"
                @update="updateConfiguration"
            />
        </div>

        <div v-if="!isViewMode" class="survey-wizard__footer">
            <v-btn
                v-if="currentStep > 0"
                outlined
                color="primary"
                @click="previousStep"
                :disabled="loading"
            >
                <v-icon left>mdi-arrow-left</v-icon>
                {{ $t('surveys.wizard.back') }}
            </v-btn>
            <v-spacer></v-spacer>

            <template v-if="currentStep < 3">
                <v-btn
                    color="primary"
                    @click="nextStep"
                    :disabled="!canAdvance || loading"
                >
                    {{ $t('surveys.wizard.next') }}
                    <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
            </template>

            <template v-if="currentStep === 3 && !isViewMode">
                <v-btn
                    outlined
                    color="primary"
                    @click="saveSurvey"
                    :loading="loading"
                    :disabled="!canSave"
                    class="mr-2"
                >
                    {{ $t('surveys.wizard.saveDraft') }}
                </v-btn>

                <v-btn
                    color="success"
                    @click="publishSurvey"
                    :loading="loading"
                    :disabled="!canSave"
                >
                    {{ $t('surveys.wizard.publish') }}
                </v-btn>
            </template>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
    ACTION_GET_QUESTION_TYPES,
    ACTION_GET_SURVEY_BY_ID,
    ACTION_POST_CREATE_SURVEY,
    ACTION_PUT_UPDATE_SURVEY,
    ACTION_PATCH_SURVEY_STATUS,
    GET_QUESTION_TYPES,
    GET_STATE_LOADING,
    SURVEYS_M,
} from "@/views/surveys/store/types/types.js";

import StepGeneralData from "./steps/step_general_data.vue";
import StepQuestions from "./steps/step_questions.vue";
import StepPreview from "./steps/step_preview.vue";
import StepConfiguration from "./steps/step_configuration.vue";

export default {
    name: "createSurveyComponent",
    components: {
        StepGeneralData,
        StepQuestions,
        StepPreview,
        StepConfiguration
    },

    data() {
        return {
            currentStep: 0,
            surveyForm: {
                title: "",
                description: "",
                validFrom: null,
                validUntil: null,
                questions: [],
                allowMultipleResponses: false
            },
            questionsAreValid: false,
            originalQuestions: []
        };
    },

    async mounted() {
        if (this.isViewMode) {
            this.currentStep = 2;
        }

        await this.getQuestionTypes();

        const surveyId = this.$route.query.id;
        if (surveyId) {
            await this.loadSurvey(surveyId);
        }
    },

    methods: {
        ...mapActions({
            getQuestionTypes: `${SURVEYS_M}${ACTION_GET_QUESTION_TYPES}`,
            getSurveyById: `${SURVEYS_M}${ACTION_GET_SURVEY_BY_ID}`,
            createSurvey: `${SURVEYS_M}${ACTION_POST_CREATE_SURVEY}`,
            updateSurvey: `${SURVEYS_M}${ACTION_PUT_UPDATE_SURVEY}`,
            patchSurveyStatus: `${SURVEYS_M}${ACTION_PATCH_SURVEY_STATUS}`,
        }),

        async loadSurvey(surveyId) {
            try {
                const resp = await this.getSurveyById(surveyId);
                if (resp.payload) {
                    const survey = resp.payload;
                    this.surveyForm = {
                        surveyId: survey.surveyId,
                        title: survey.title || "",
                        description: survey.description || "",
                        validFrom: survey.validFrom || null,
                        validUntil: survey.validUntil || null,
                        questions: this.normalizeLoadedQuestions(survey.questions || []),
                        allowMultipleResponses: survey.allowMultipleResponses || false
                    };
                    this.originalQuestions = JSON.parse(JSON.stringify(this.surveyForm.questions));
                }
            } catch (error) {
                console.error("Error loading survey:", error);
            }
        },

        normalizeLoadedQuestions(questions) {
            return questions.map(q => ({
                questionId: q.questionId,
                type: q.type,
                title: q.title,
                required: !!q.required,
                config: q.config || {},
                options: (q.options || []).map(o => ({
                    optionId: o.optionId,
                    label: o.label,
                    position: o.position
                }))
            }));
        },

        updateGeneralData(data) {
            Object.assign(this.surveyForm, {
                title: data.title,
                description: data.description,
                validFrom: data.validFrom || null,
                validUntil: data.validUntil || null
            });
        },

        updateQuestions(questions) {
            this.surveyForm.questions = questions;
        },

        setQuestionsValidity(valid) {
            this.questionsAreValid = valid;
        },

        updateConfiguration(config) {
            this.surveyForm.allowMultipleResponses = config.allowMultipleResponses;
        },

        nextStep() {
            if (this.currentStep < 3) {
                this.currentStep++;
            }
        },

        previousStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
            }
        },

        async saveSurvey() {
            await this.submitSurvey(false);
            this.$router.push({ name: 'surveys' });
        },

        async publishSurvey() {
            try {
                const surveyId = await this.submitSurvey(true);
                await this.patchSurveyStatus({ surveyId, status: 'PUBLISHED' });
                this.$router.push({ name: 'surveys' });
            } catch (error) {
                console.error('Error publicando encuesta:', error);
            }
        },

        async submitSurvey(silent = false) {
            const payload = {
                survey: {
                    surveyId: this.surveyForm.surveyId,
                    title: this.surveyForm.title,
                    description: this.surveyForm.description,
                    allowMultipleResponses: this.surveyForm.allowMultipleResponses,
                    validFrom: this.surveyForm.validFrom,
                    validUntil: this.surveyForm.validUntil
                },
                questions: this.surveyForm.questions,
                originalQuestions: this.originalQuestions,
                silent
            };

            let surveyId = this.surveyForm.surveyId;
            if (this.isEditMode) {
                const resp = await this.updateSurvey(payload);
                if (resp && resp.payload && resp.payload.surveyId) surveyId = resp.payload.surveyId;
            } else {
                const resp = await this.createSurvey(payload);
                if (resp && resp.payload && resp.payload.surveyId) surveyId = resp.payload.surveyId;
            }
            return surveyId;
        }
    },

    computed: {
        ...mapGetters({
            questionTypes: `${SURVEYS_M}${GET_QUESTION_TYPES}`,
            loading: `${SURVEYS_M}${GET_STATE_LOADING}`,
        }),

        isEditMode() {
            return !!this.$route.query.id && this.$route.query.mode === 'edit';
        },

        isViewMode() {
            return this.$route.query.mode === 'view';
        },

        headerTitle() {
            if (this.isViewMode) return this.$t('surveys.wizard.titleView');
            if (this.isEditMode) return this.$t('surveys.wizard.titleEdit');
            return this.$t('surveys.wizard.titleCreate');
        },

        steps() {
            return [
                { label: this.$t('surveys.wizard.stepLabels.generalData') },
                { label: this.$t('surveys.wizard.stepLabels.questions') },
                { label: this.$t('surveys.wizard.stepLabels.preview') },
                { label: this.$t('surveys.wizard.stepLabels.configuration') }
            ];
        },

        allQuestionsHaveText() {
            return this.surveyForm.questions.length > 0 &&
                this.surveyForm.questions.every(q => (q.title || '').trim() !== '');
        },

        hasTitle() {
            return (this.surveyForm.title || '').trim() !== '';
        },

        areDatesValid() {
            const now = new Date();
            const from = this.surveyForm.validFrom ? new Date(this.surveyForm.validFrom) : null;
            const to = this.surveyForm.validUntil ? new Date(this.surveyForm.validUntil) : null;
            if (!from || !to) return false;
            if (from < now) return false;
            if (to < now) return false;
            if (to <= from) return false;
            return true;
        },

        canAdvance() {
            switch (this.currentStep) {
                case 0:
                    return this.hasTitle && this.areDatesValid;
                case 1:
                    return this.questionsAreValid;
                case 2:
                    return this.allQuestionsHaveText;
                default:
                    return true;
            }
        },

        canSave() {
            return this.hasTitle && this.areDatesValid && this.allQuestionsHaveText;
        }
    }
};
</script>

<style lang="css" scoped>
    .survey-wizard {
        width: 100%;
        max-width: 100%;
        padding: 20px;
        box-sizing: border-box;
    }

    .survey-wizard__header {
        display: flex;
        align-items: center;
        margin-bottom: 24px;
    }

    .survey-wizard__header h1 {
        font-family: "Amsi Pro", sans-serif;
        color: var(--v-primary-base);
        font-weight: normal;
        margin: 0;
    }

    .survey-wizard__steps {
        display: flex;
        flex-wrap: wrap;
        gap: 16px 24px;
        border-bottom: 1px solid #ECEAF2;
        margin-bottom: 26px;
        padding-bottom: 0;
    }

    .survey-wizard__step {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #9A9AA6;
        padding-bottom: 13px;
        border-bottom: 2px solid transparent;
        cursor: default;
    }

    .survey-wizard__step--active {
        color: #6E17A5;
        border-bottom-color: #6E17A5;
    }

    .survey-wizard__step--completed {
        color: #1F1F2B;
    }

    .survey-wizard__step-number {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #F1F1F4;
        color: #6B6B76;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
    }

    .survey-wizard__step--active .survey-wizard__step-number {
        background: #6E17A5;
        color: white;
    }

    .survey-wizard__step--completed .survey-wizard__step-number {
        background: #E4F7EC;
        color: #1E9E5C;
    }

    .survey-wizard__content {
        min-height: 400px;
        width: 100%;
    }

    .survey-wizard__footer {
        display: flex;
        align-items: center;
        margin-top: 24px;
        border-top: 1px solid #ECEAF2;
        padding-top: 20px;
        padding-bottom: 20px;
    }
</style>
